import { create } from 'zustand';
import { getDueTasks } from '../api/generated/due-tasks/due-tasks';
import type { FacilityFlowCoreModulesPlanningDtosDueTasksDueTaskDto } from '../api/generated/models';

const api = getDueTasks();

export interface ObjectWeekMinutes {
  due: number;
  planned: number;
}

interface DueTasksState {
  dueTasks: FacilityFlowCoreModulesPlanningDtosDueTasksDueTaskDto[];
  loading: boolean;
  error?: string;
  /**
   * Scheduled work per object id in the loaded week (FAFD-56): minutes due according to the
   * schedule rules and minutes of those tasks that are planned. Empty when the fetch failed.
   */
  objectDueMinutes: Record<string, ObjectWeekMinutes>;

  fetchDueTasks: (year: number, week: number) => Promise<void>;
  fetchObjectDueMinutes: (year: number, week: number) => Promise<void>;
  /**
   * Optimistic update after a task was assigned to a day. Scheduled tasks lose one open
   * occurrence — the exact due date first, then the day-free week occurrence, then the
   * day-free month occurrence of the planned date — and only disappear once everything
   * is covered. Tasks without a schedule rule stay in the list (planned repeatedly).
   */
  markPlanned: (taskId: string, date: string) => void;
}

export const useDueTasksStore = create<DueTasksState>((set) => ({
  dueTasks: [],
  loading: false,
  objectDueMinutes: {},

  fetchObjectDueMinutes: async (year: number, week: number) => {
    try {
      const response = await api.getApiV1DueTasksObjectMinutes({ year, week });
      const byObject: Record<string, ObjectWeekMinutes> = {};
      for (const row of response ?? []) {
        if (row.objectId) byObject[row.objectId] = { due: row.dueMinutes ?? 0, planned: row.plannedMinutes ?? 0 };
      }
      set({ objectDueMinutes: byObject });
    } catch (err) {
      // why: `error` belongs to the due-tasks panel of the planning page; the objects list
      // only loses its "Eingeplant" figures, so clear them instead of leaking an error there.
      set({ objectDueMinutes: {} });
      throw err;
    }
  },

  fetchDueTasks: async (year: number, week: number) => {
    set({ loading: true, error: undefined });
    try {
      const response = await api.getApiV1DueTasks({ year, week });
      set({ dueTasks: response ?? [], loading: false });
    } catch (err) {
      set({
        error: 'dueTasks.fetchFailed',
        loading: false,
      });
      throw err;
    }
  },

  markPlanned: (taskId: string, date: string) => {
    set((state) => ({
      dueTasks: state.dueTasks.reduce<
        FacilityFlowCoreModulesPlanningDtosDueTasksDueTaskDto[]
      >((acc, task) => {
        if (task.taskId !== taskId || !task.hasSchedule) {
          acc.push(task);
          return acc;
        }

        let openDates = task.openDates ?? [];
        let weekOpen = task.weekOpen ?? false;
        let openMonths = task.openMonths ?? [];

        if (openDates.includes(date)) {
          openDates = openDates.filter((d) => d !== date);
        } else if (weekOpen) {
          weekOpen = false;
        } else {
          const plannedMonth = new Date(`${date}T00:00:00`).getMonth() + 1;
          openMonths = openMonths.filter((m) => m !== plannedMonth);
        }

        if (openDates.length > 0 || weekOpen || openMonths.length > 0) {
          acc.push({ ...task, openDates, weekOpen, openMonths });
        }

        return acc;
      }, []),
    }));
  },
}));
